import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useApoAuth } from "..";
import {
  carRentalSearchFormChange,
  carRentalSearchFormUpdate,
  carRentalSearchResultsFailed,
  carRentalSearchResultsIdle,
  carRentalSearchResultsLoading,
  carRentalSearchResultsUpdate,
} from "../../actions/search/car_rental_search";
import { changeServiceSessionIds } from "../../actions/search/session_ids";
import _ from "lodash";
import {
  fetchSavedCarRentalSearchResults,
  searchRequestCarRentalPOST,
} from "../../api/carRental";
import { toast } from "react-toastify";
import { carRentalErrorMsgs } from "../../errors";
import {
  retrieveSearchSessionMetadata,
  storeSearchSessionMetadata,
} from "../../api";
import { TranslationContext } from "../../umbilical_tools";

export function useCarRentalActions() {
  const dispatch = useDispatch();

  const onFormUpdate = useCallback(
    (form) => dispatch(carRentalSearchFormUpdate({ form })),
    [dispatch]
  );

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("CAR", value)),
    [dispatch]
  );

  const onSearchResultsLoading = useCallback(
    () => dispatch(carRentalSearchResultsLoading()),
    [dispatch]
  );

  const onCarRentalSearchFormChange = useCallback(
    (key, value) => dispatch(carRentalSearchFormChange(key, value)),
    [dispatch]
  );

  const onCarRentalSearchResultsUpdate = useCallback(
    (data) => dispatch(carRentalSearchResultsUpdate(data)),
    [dispatch]
  );

  const onFailed = useCallback(() => {
    dispatch(carRentalSearchResultsIdle());
    dispatch(carRentalSearchResultsFailed());
  }, [dispatch]);

  return {
    onFormUpdate,
    onChangeSessionIDs,
    onSearchResultsLoading,
    onFailed,
    onCarRentalSearchFormChange,
    onCarRentalSearchResultsUpdate,
  };
}

function actionFailed({ history, onFailed, errorMsg }) {
  onFailed();
  toast.warning(errorMsg, {
    duration: 3000,
  });
  history.push("/");
}

function getCarRentalSearchPayload({ form, market_type, source_entity }) {
  const output = {
    pickup_date: form.pick_up_date,
    return_date: form.drop_off_date,
    pickup_lat:
      form.pick_up_point.pick_up_point_meta.computed_data.geodata.lat.toFixed(
        6
      ),
    pickup_lon:
      form.pick_up_point.pick_up_point_meta.computed_data.geodata.lng.toFixed(
        6
      ),
    return_lat: form.differentDropOff
      ? form.drop_off_point.drop_off_point_meta.computed_data.geodata.lat.toFixed(
          6
        )
      : form.pick_up_point.pick_up_point_meta.computed_data.geodata.lat.toFixed(
          6
        ),
    return_lon: form.differentDropOff
      ? form.drop_off_point.drop_off_point_meta.computed_data.geodata.lng.toFixed(
          6
        )
      : form.pick_up_point.pick_up_point_meta.computed_data.geodata.lng.toFixed(
          6
        ),
    driver_age: form.driver_age,
    language: form.language,
    nationality: form.driver_nationality,
    market_type,
    source_entity,
  };
  return output;
}

export function useCarRentalsInitSearch({ setLoadingSearchBtn = () => {} }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useContext(TranslationContext);

  const {
    onFormUpdate,
    onChangeSessionIDs,
    onSearchResultsLoading,
    onFailed,
    onCarRentalSearchFormChange,
  } = useCarRentalActions();

  const { source_entity, market_type } = useApoAuth();

  async function initSearch(form) {
    if (_.isEmpty(form)) {
      return;
    }
    onFormUpdate(form);
    onSearchResultsLoading();

    const payload = getCarRentalSearchPayload({
      form,
      market_type,
      source_entity,
    });

    onCarRentalSearchFormChange("searchPayload", payload);

    const response = await searchRequestCarRentalPOST({
      payload,
      onError: () => setLoadingSearchBtn(false),
    });

    if (!response || !response?.session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: carRentalErrorMsgs(t).searchForm.init_search,
      });
      setLoadingSearchBtn(false);
      return;
    }

    const search_session_id = _.get(response, "session_id", "");

    const searchSessionMetaResult = await storeSearchSessionMetadata({
      service_type: "CAR",
      session_data: {
        search_session_id,
        search_form: form,
        searchPayload: payload,
      },
    });

    if (
      _.isEmpty(_.get(searchSessionMetaResult, "session_data", {})) ||
      !searchSessionMetaResult.session_id
    ) {
      actionFailed({
        history,
        onFailed,
        errorMsg: carRentalErrorMsgs(t).general.no_session,
      });
      setLoadingSearchBtn(false);

      return;
    }

    const { session_id: page_session_id } = searchSessionMetaResult;
    history.push(`/car-rentals/${page_session_id}`);

    onChangeSessionIDs({ page_session_id, search_session_id });

    dispatch(carRentalSearchResultsIdle());

    setLoadingSearchBtn(false);
  }

  return [initSearch];
}

export function useCarRentalsGetSessionMeta({ page_session_id }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useContext(TranslationContext);
  const { onFormUpdate, onChangeSessionIDs, onFailed } = useCarRentalActions();

  const search_session_id = useSelector(
    (state) => state.SessionIds.CAR.search_session_id
  );

  const pageSessionFetcher = useCallback(
    ({ page_session_id }) => {
      retrieveSearchSessionMetadata({ page_session_id })
        .then(async (data) => {
          if (!data) {
            actionFailed({
              history,
              onFailed,
              errorMsg: carRentalErrorMsgs(t).searchForm.no_session,
            });
            return;
          }
          const form = _.get(data, "session_data.data.search_form", {});

          onChangeSessionIDs({
            page_session_id,
            search_session_id: _.get(
              data,
              "session_data.data.search_session_id",
              ""
            ),
          });

          await onFormUpdate(form);
        })
        .catch(() => {
          actionFailed({
            history,
            onFailed,
            errorMsg: carRentalErrorMsgs(t).searchForm.no_session,
          });
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!page_session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: carRentalErrorMsgs(t).searchForm.no_session,
      });
      return;
    }
    if (!search_session_id) {
      pageSessionFetcher({ page_session_id });
      return;
    }
  }, [search_session_id, page_session_id]);

  return { search_session_id };
}

export async function useCarRentalFetcher({ search_session_id }) {
  const history = useHistory();
  const { t } = useContext(TranslationContext);
  const { onFailed, onSearchResultsLoading, onCarRentalSearchResultsUpdate } =
    useCarRentalActions();

  const carRentals = useSelector((state) =>
    _.get(state.CarRentalSearchResults, "car_rentals", [])
  );

  async function fetchData() {
    onSearchResultsLoading();

    const response = await fetchSavedCarRentalSearchResults({
      search_session_id,
      onError: () => {
        actionFailed({
          history,
          onFailed,
          errorMsg: carRentalErrorMsgs(t).searchForm.init_search,
        });
      },
    });

    if (!response) {
      onFailed();
      toast.warning(carRentalErrorMsgs(t).resultsList.no_results, {
        duration: 3000,
      });
      history.push("/");
      return false;
    }

    onCarRentalSearchResultsUpdate({
      car_rentals: _.get(response, "results", []),
      api_uid: _.get(response, "api_uid", ""),
      count: _.get(response, "count", 0),
      loading: false,
      failed: false,
    });
  }

  useEffect(() => {
    if (!search_session_id) {
      return;
    }
    if (carRentals.length) {
      return;
    }

    fetchData();
  }, [search_session_id]);
}

export function useCarRentalPoller({ search_session_id }) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { t } = useContext(TranslationContext);

  const { onSearchResultsLoading, onFailed, onCarRentalSearchResultsUpdate } =
    useCarRentalActions();

  const maxPollCount = 7;

  function rePoller({ pollCount }) {
    setTimeout(() => poller({ pollCount }), 3000);
  }

  async function poller({ pollCount = 0 }) {
    const poll_response = await fetchSavedCarRentalSearchResults({
      search_session_id,
      onError: (error) => {
        if (error.response?.status === 400) {
          return { results: [], status: "working", error: { status: null } };
        }
        return {
          error: {
            status: error.response?.status,
            message: error.response?.data.message,
          },
        };
      },
    });

    if (
      poll_response.error?.status ||
      (pollCount >= maxPollCount && poll_response.results.length === 0)
    ) {
      actionFailed({
        history,
        onFailed,
        errorMsg: poll_response.error?.status
          ? poll_response.error.message
          : carRentalErrorMsgs(t).resultsList.no_results,
      });
      return;
    }
    const is_done = _.get(poll_response, "status", "") === "done";
    const results = _.get(poll_response, "results", []);
    const has_results = results.length > 0;
    if (is_done && !has_results) {
      actionFailed({
        history,
        onFailed,
        errorMsg: carRentalErrorMsgs(t).resultsList.no_results,
      });
      return;
    }
    if (has_results) {
      onCarRentalSearchResultsUpdate({ car_rentals: results, loading: false });
    }
    if (!is_done && pollCount < maxPollCount) {
      rePoller({ pollCount: pollCount + 1 });
    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!search_session_id || !loading) {
      return;
    }
    onSearchResultsLoading();

    poller({ pollCount: 0 });
  }, [search_session_id, loading]);
}
